import { useMutation } from '@tanstack/react-query'
import { PersonalDetailsUrls } from '../../../Utils/network'
import { type EditContactDetailsError } from '@mobi/api-types'
import { post } from '../../../Utils/network'
import { addToast } from '@mobi/component-library/Feedback/Toast'

type Props = {
  accountNumber: number
  betAccountHolderNumber: number | null
}

export type ContactDetailsBody = {
  contactDetails: {
    email: string
    mobile: {
      countryCode: string
      phoneNumber: string
    }
    homePhone: {
      countryCode: string
      areaCode?: string
      phoneNumber: string
    }
    workPhone: {
      countryCode: string
      areaCode?: string
      phoneNumber: string
    }
  }
  otpCode?: string
  userAction?: string
}

export function useEditContactDetails({ accountNumber, betAccountHolderNumber }: Props) {
  const editContactDetails = useMutation<void, EditContactDetailsError, ContactDetailsBody>({
    mutationFn: async body => {
      await post(
        PersonalDetailsUrls.editContactDetails(accountNumber, betAccountHolderNumber),
        body
      )
      triggerNonBlockingCognitoContactSync()
    },
    onSuccess: () => {
      addToast({
        id: 'contact-details-success',
        isDismissable: true,
        isFullWidth: true,
        title: 'Details updated successfully.',
        icon: 'success',
      })
    },
    onError: error => {
      // Do not show the error toast if the error is caused by exhausted validation attempts
      // Instead, display a modal prompting the user to request a new code
      if (error.status === 429) return

      addToast({
        id: 'contact-details-error',
        isDismissable: true,
        isFullWidth: true,
        title: 'We were unable to process your request. Please try again later.',
        icon: 'error',
      })
    },
  })

  return editContactDetails
}

// when the user changes their contact details we want to trigger a sync
// immediately to cognito instead of waiting for the scheduled run. This
// will not block the contact details update, and any errors will
// be swallowed - we don't care in this context if it succeeds
async function triggerNonBlockingCognitoContactSync() {
  try {
    await post('/api/mfa/sync-auth-contact-details', {})
  } catch {
    // ... no-op - ignore errors
  }
  return Promise.resolve()
}
